import { useMemo, useEffect } from 'react';
import { useGetCashFundForContributionQuery } from '../../CashFunds.graphql.generated';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useCashFundsTelemetry } from '../../CashFunds.telemetry';

interface CashFundGuestControllerProps {
  readonly fundId: string | null;
}

export const useCashFundGuestController = ({ fundId }: CashFundGuestControllerProps) => {
  const { formatCurrency } = useCurrencyFormatter();
  const queryParams = useQueryParams();
  const telemetry = useCashFundsTelemetry();

  const guestKeyParam = typeof queryParams.guestKey === 'string' ? queryParams.guestKey : null;

  const { data, loading, error } = useGetCashFundForContributionQuery({
    batchMode: 'fast',
    variables: {
      input: {
        cashFundId: fundId || '',
        guestKey: guestKeyParam,
      },
    },
    skip: !fundId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (error) {
      telemetry.queryError('getCashFundForContribution', error, fundId || undefined);
    }
  }, [error, telemetry, fundId]);

  const fund = data?.getCashFundForContribution;

  const currencyCode = useMemo(() => {
    return fund?.cashFund?.currencyCode || DEFAULT_CURRENCY;
  }, [fund]);

  const totalContributedAmount = fund?.cashFund?.publicTotalContributedAmount;
  const totalGoal = fund?.cashFund?.publicTotalGoal;
  const fixedContributionAmount = fund?.cashFund?.fixedContributionAmount;

  const totalRaised = useMemo(() => {
    if (!totalContributedAmount) {
      return 0;
    }
    return parseFloat(totalContributedAmount.floatingPointDecimalString || '0');
  }, [totalContributedAmount]);

  const goalAmount = useMemo(() => {
    if (!totalGoal) {
      return null;
    }
    return parseFloat(totalGoal.floatingPointDecimalString || '0');
  }, [totalGoal]);

  const progressPercentage = useMemo(() => {
    if (!goalAmount || goalAmount === 0) {
      return 0;
    }
    return Math.min(100, Math.round((totalRaised / goalAmount) * 100));
  }, [totalRaised, goalAmount]);

  const formattedTotalRaised = useMemo(() => {
    return formatCurrency({
      priceFloatingPointDecimalString: totalRaised.toString(),
      priceCurrencyCode: currencyCode,
      formatForm: 'short',
      hideZeroDecimals: true,
    });
  }, [totalRaised, currencyCode, formatCurrency]);

  const formattedGoal = useMemo(() => {
    if (!goalAmount) {
      return null;
    }
    return formatCurrency({
      priceFloatingPointDecimalString: goalAmount.toString(),
      priceCurrencyCode: currencyCode,
      formatForm: 'short',
      hideZeroDecimals: true,
    });
  }, [goalAmount, currencyCode, formatCurrency]);

  const formattedPerPerson = useMemo(() => {
    if (!fixedContributionAmount) {
      return null;
    }
    const amount = parseFloat(fixedContributionAmount.floatingPointDecimalString || '0');
    if (amount === 0) {
      return null;
    }
    return formatCurrency({
      priceFloatingPointDecimalString: amount.toString(),
      priceCurrencyCode: currencyCode,
      formatForm: 'short',
      hideZeroDecimals: true,
    });
  }, [fixedContributionAmount, currencyCode, formatCurrency]);

  const inviteFullName = fund?.invite?.fullName || null;
  const inviteEmail = fund?.invite?.email || null;
  const hostFirstName = fund?.host?.firstName || null;
  const hostLastName = fund?.host?.lastName || null;
  const hostName = useMemo(() => {
    if (hostFirstName && hostLastName) {
      return `${hostFirstName} ${hostLastName}`;
    }
    return hostFirstName || hostLastName || null;
  }, [hostFirstName, hostLastName]);

  return {
    fund,
    loading,
    error,
    goalAmount,
    totalRaised,
    progressPercentage,
    formattedTotalRaised,
    formattedGoal,
    inviteFullName,
    inviteEmail,
    hostName,
    formattedPerPerson,
  };
};
